/**
 * Pending stage-moves cache.
 *
 * The Kanban board's optimistic state lives only in component state, so a
 * drag → click ticket → click back sequence can show the ticket in its
 * previous lane: the board unmounts, refetches on remount, and the API may
 * still be reporting the pre-move stage (Kafka delay, read-after-write lag).
 *
 * This module persists the optimistic move in sessionStorage so the board
 * can re-apply it after any refetch, until the backend's response confirms
 * the move (or the entry expires).
 */

const STORAGE_KEY = 'tickets:pendingMoves:v1';
const TTL_MS = 30_000;

export interface PendingMove {
  ticketId: string;
  stageId: string;
  status?: string;
  ts: number;
}

function safeRead(): PendingMove[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter(
      (m): m is PendingMove =>
        !!m &&
        typeof m.ticketId === 'string' &&
        typeof m.stageId === 'string' &&
        typeof m.ts === 'number' &&
        now - m.ts < TTL_MS,
    );
  } catch {
    return [];
  }
}

function safeWrite(list: PendingMove[]): void {
  if (typeof window === 'undefined') return;
  try {
    if (list.length === 0) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    }
  } catch {
    // sessionStorage may throw in private mode / over-quota — fail open.
  }
}

export const pendingMoves = {
  set(ticketId: string, stageId: string, status?: string): void {
    const list = safeRead().filter((m) => m.ticketId !== ticketId);
    list.push({ ticketId, stageId, status, ts: Date.now() });
    safeWrite(list);
  },

  clear(ticketId: string): void {
    safeWrite(safeRead().filter((m) => m.ticketId !== ticketId));
  },

  getAll(): PendingMove[] {
    const list = safeRead();
    safeWrite(list);
    return list;
  },

  prune(): void {
    safeWrite(safeRead());
  },
};
